/**
 * Response Analytics Panel — Feature 4
 *
 * Rendered inside the CM Bulk Campaign workspace under the "Response Analytics"
 * tab.  Consumes GET /api/bulk/campaigns/{id}/response-stats and shows:
 *
 *   • Summary tiles (Invited / Responded / Response Rate / Avg Response Time)
 *   • No-response list (creators still on 'invited' after 48h) with a
 *     one-click Send Reminder button that fires a WhatsApp reminder template.
 *   • By-category breakdown table
 *   • By-region breakdown table
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Users, TrendingUp, Clock, AlertTriangle, MessageCircle, MapPin, Layers,
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const rateColor = (r) => (r >= 60 ? '#00C972' : r >= 30 ? '#FFB800' : '#FF3B30');

const Tile = ({ icon: Icon, label, value, sub, color = '#FF451A', testid }) => (
  <div
    className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4"
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">
      {Icon && <Icon size={11} style={{ color }} />} {label}
    </div>
    <div className="text-2xl font-bold font-mono" style={{ color }}>{value}</div>
    {sub && <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-0.5">{sub}</div>}
  </div>
);

export const ResponseAnalyticsPanel = ({ campaignId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_URL}/api/bulk/campaigns/${campaignId}/response-stats`,
        { withCredentials: true }
      );
      setStats(data);
    } catch {
      toast.error('Failed to load response stats');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [campaignId]);

  const sendReminder = async (row) => {
    setSending((s) => ({ ...s, [row.row_id]: true }));
    try {
      // Reuse the existing per-row WhatsApp send.  Template code
      // 'reminder_no_response' should exist; if not, fall back to the
      // generic 'campaign_reminder'.
      await axios.post(
        `${API_URL}/api/whatsapp/creators/${row.row_id}/send`,
        { template_code: 'reminder_no_response' },
        { withCredentials: true }
      );
      toast.success(`Reminder sent to ${row.creator_name || row.instagram_handle || 'creator'}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Reminder failed — check WhatsApp template setup');
    } finally {
      setSending((s) => ({ ...s, [row.row_id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#6B6B6B] dark:text-zinc-400 py-8" data-testid="response-analytics-loading">
        <Loader2 size={14} className="animate-spin" /> Loading response analytics…
      </div>
    );
  }
  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="response-analytics-panel">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="response-summary">
        <Tile
          icon={Users}
          label="Total Invited"
          value={stats.invited}
          sub={`${stats.total_creators} row(s) total`}
          color="#FF451A"
          testid="stat-invited"
        />
        <Tile
          icon={MessageCircle}
          label="Responded"
          value={stats.responded}
          sub={`${stats.accepted} accepted · ${stats.declined} declined`}
          color="#00C972"
          testid="stat-responded"
        />
        <Tile
          icon={TrendingUp}
          label="Response Rate"
          value={`${stats.response_rate}%`}
          color={rateColor(stats.response_rate)}
          testid="stat-response-rate"
        />
        <Tile
          icon={Clock}
          label="Avg Response Time"
          value={stats.avg_response_time_hours ? `${stats.avg_response_time_hours}h` : '—'}
          sub="From invite to first reply"
          color="#9B6EFF"
          testid="stat-avg-response-time"
        />
      </div>

      {/* No-response list */}
      <div data-testid="no-response-section">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} className="text-[#FFB800]" />
          <h4 className="text-sm font-bold uppercase tracking-wider text-[#0F0F0F] dark:text-white">
            No Response ({stats.no_response})
          </h4>
          <span className="text-[11px] font-mono text-[#6B6B6B] dark:text-zinc-500 uppercase tracking-wider">
            {`Older than ${stats.no_response_threshold_hours}h`}
          </span>
        </div>
        {stats.no_response_rows.length === 0 ? (
          <div className="text-sm text-[#6B6B6B] dark:text-zinc-500 py-4 px-4 border border-dashed border-black/[0.08] dark:border-white/5 rounded-xl">
            Everyone with a stale invite has been chased. Nice work.
          </div>
        ) : (
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-4 py-2 font-normal">Creator</th>
                  <th className="text-left px-4 py-2 font-normal">Handle</th>
                  <th className="text-left px-4 py-2 font-normal">Hours pending</th>
                  <th className="text-right px-4 py-2 font-normal">Action</th>
                </tr>
              </thead>
              <tbody>
                {stats.no_response_rows.map((row) => (
                  <tr key={row.row_id} className="border-t border-black/[0.05] dark:border-white/5" data-testid={`no-response-row-${row.row_id}`}>
                    <td className="px-4 py-2 text-[#0F0F0F] dark:text-white">{row.creator_name || '—'}</td>
                    <td className="px-4 py-2 text-[#6B6B6B] dark:text-zinc-400">@{row.instagram_handle || '—'}</td>
                    <td className="px-4 py-2 text-[#6B6B6B] dark:text-zinc-400 font-mono">{row.hours_since_outreach ?? '—'}h</td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        onClick={() => sendReminder(row)}
                        disabled={sending[row.row_id]}
                        className="bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/30 hover:bg-[#FF451A]/20 rounded-full px-3 py-1 text-xs font-medium disabled:opacity-40"
                        data-testid={`send-reminder-${row.row_id}`}
                      >
                        {sending[row.row_id] ? <Loader2 size={12} className="animate-spin" /> : 'Send Reminder'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* By category */}
      <BreakdownTable
        title="By Category"
        icon={Layers}
        rows={stats.by_category}
        keyField="category"
        testid="by-category-table"
      />

      {/* By region */}
      <BreakdownTable
        title="By Region"
        icon={MapPin}
        rows={stats.by_region}
        keyField="state"
        testid="by-region-table"
      />
    </div>
  );
};

const BreakdownTable = ({ title, icon: Icon, rows, keyField, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={14} className="text-[#FF451A]" />
      <h4 className="text-sm font-bold uppercase tracking-wider text-[#0F0F0F] dark:text-white">{title}</h4>
    </div>
    {rows.length === 0 ? (
      <div className="text-sm text-[#6B6B6B] dark:text-zinc-500 py-3 px-4 border border-dashed border-black/[0.08] dark:border-white/5 rounded-xl">
        No breakdown data yet.
      </div>
    ) : (
      <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-4 py-2 font-normal capitalize">{keyField}</th>
              <th className="text-right px-4 py-2 font-normal">Invited</th>
              <th className="text-right px-4 py-2 font-normal">Responded</th>
              <th className="text-right px-4 py-2 font-normal">Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[keyField]} className="border-t border-black/[0.05] dark:border-white/5">
                <td className="px-4 py-2 text-[#0F0F0F] dark:text-white capitalize">{r[keyField]}</td>
                <td className="px-4 py-2 text-right text-[#6B6B6B] dark:text-zinc-400 font-mono">{r.invited}</td>
                <td className="px-4 py-2 text-right text-[#6B6B6B] dark:text-zinc-400 font-mono">{r.responded}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold" style={{ color: rateColor(r.response_rate) }}>
                  {r.response_rate}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default ResponseAnalyticsPanel;
